import { useEffect, useState } from 'react'
import { FileText, Layers, Loader2 } from 'lucide-react'
import { opcoApi } from '../../../services/api'
import { useToast } from '../../../context/ToastContext'
import { Modal, PanelState, errorMessage, primaryBtn, secondaryBtn } from './shared'
import { canApplyWorkMove, hasWork, toggleKey, workMoveSummary } from './workMoveHelpers'

// "Bring their existing work": what this person made before they joined the OpCo (requests and templates
// that no OpCo owns). Preview first (nothing is written), untick anything that should stay where it is,
// then Move. Every run can be undone from "Moved work" on the Members panel. Requests that were posted,
// delivered or are still extracting, and templates other people also use, are listed but never moved.
export default function MoveWorkModal({ opco, person, initialPlan = null, onClose, onDone }) {
  const toast = useToast()
  const [plan, setPlan] = useState(initialPlan)
  const [excluded, setExcluded] = useState(() => new Set())
  const [included, setIncluded] = useState(() => new Set())
  const [loading, setLoading] = useState(!initialPlan)
  const [error, setError] = useState(null)
  const [applying, setApplying] = useState(false)

  useEffect(() => {
    if (initialPlan) return undefined
    let cancelled = false
    opcoApi.previewWorkMove(person.id, opco.id)
      .then((res) => { if (!cancelled) setPlan(res?.data || null) })
      .catch((err) => { if (!cancelled) setError(errorMessage(err, 'Could not build the preview')) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [initialPlan, person.id, opco.id])

  const apply = async () => {
    setApplying(true)
    try {
      const res = await opcoApi.applyWorkMove({
        user_id: person.id,
        opco_id: opco.id,
        plan_hash: plan.plan_hash,
        excluded_keys: [...excluded],
        include_keys: [...included],
      })
      const run = res?.data?.run || {}
      toast.success(
        'Work moved',
        `${run.moved ?? 0} moved${run.skipped ? `, ${run.skipped} skipped` : ''}${run.failed ? `, ${run.failed} failed` : ''}. You can undo this under Moved work.`,
      )
      await onDone?.()
    } catch (err) {
      const code = err?.response?.data?.code
      if (code === 'stale_preview' && err.response.data.plan) {
        setPlan(err.response.data.plan)
        setExcluded(new Set())
        setIncluded(new Set())
        toast.warning('The list changed', 'Something changed since you previewed. Check the new list and move again.')
      } else if (code === 'run_in_progress') {
        toast.warning('Already running', 'Another apply or undo is running. Try again in a moment.')
      } else {
        toast.error('Could not move the work', errorMessage(err))
      }
    } finally {
      setApplying(false)
    }
  }

  const rows = plan?.rows || []
  const blocked = plan?.blocked || []
  const needs = plan?.needs_decision || []
  const includable = needs.filter((n) => n.can_include)
  const leftAlone = [...blocked, ...needs.filter((n) => !n.can_include)]
  const more = plan?.summary?.more_requests || 0

  return (
    <Modal
      title={`Bring ${person.label}'s existing work into ${opco.name}`}
      onClose={applying ? undefined : onClose}
      wide
      labelledBy="opco-move-work-title"
      footer={(
        <>
          <button type="button" onClick={onClose} disabled={applying} className={secondaryBtn}>
            {plan && !hasWork(plan) ? 'Close' : 'Not now'}
          </button>
          <button type="button" onClick={apply} disabled={!plan || !canApplyWorkMove(plan, excluded, applying, included)} className={primaryBtn}>
            {applying && <Loader2 className="h-4 w-4 animate-spin" />}
            Move
          </button>
        </>
      )}
    >
      <PanelState loading={loading} error={error} empty={!plan || !hasWork(plan)} emptyText="Nothing to move: this person has no earlier work that is missing an OpCo.">
        <div className="space-y-4">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            These are the requests and templates {person.label} created that no OpCo owns yet. Nothing changes until you press Move, and you can undo the run afterwards.
          </p>
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100" role="status">{workMoveSummary(plan, excluded, opco.code, included)}</p>
          {more > 0 && (
            <p className="text-xs text-amber-700 dark:text-amber-400">
              {more} more {more === 1 ? 'request is' : 'requests are'} waiting: one run moves up to 500. Preview again after moving.
            </p>
          )}

          {rows.length > 0 && (
            <div className="max-h-72 overflow-auto rounded-lg border border-gray-100 dark:border-gray-800">
              <ul className="divide-y divide-gray-100 dark:divide-gray-800">
                {rows.map((r) => (
                  <li key={r.key} className="flex items-center gap-3 px-3 py-2 text-sm">
                    <input
                      type="checkbox"
                      aria-label={`Move ${r.label}`}
                      checked={!excluded.has(r.key)}
                      onChange={() => setExcluded((prev) => toggleKey(prev, r.key))}
                    />
                    {r.entity_type === 'template'
                      ? <Layers className="h-4 w-4 flex-shrink-0 text-gray-400" aria-label="Template" />
                      : <FileText className="h-4 w-4 flex-shrink-0 text-gray-400" aria-label="Request" />}
                    <span className="min-w-0 flex-1 truncate text-gray-900 dark:text-gray-100">{r.label}</span>
                    {r.status && <span className="text-xs text-gray-500">{r.status}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {includable.length > 0 && (
            <div className="rounded-lg border border-amber-200 dark:border-amber-900/40 px-3 py-2 text-xs">
              <p className="font-semibold text-amber-800 dark:text-amber-300">
                Templates other requests also use ({includable.length}): tick to move them anyway
              </p>
              <p className="mt-0.5 text-gray-500 dark:text-gray-400">
                The template moves to {opco.name}. The other requests that use it stay where they are.
              </p>
              <ul className="mt-1 max-h-40 space-y-1 overflow-auto">
                {includable.map((n) => (
                  <li key={n.key} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      aria-label={`Move ${n.label} anyway`}
                      checked={included.has(n.key)}
                      onChange={() => setIncluded((prev) => toggleKey(prev, n.key))}
                    />
                    <Layers className="h-4 w-4 flex-shrink-0 text-gray-400" aria-label="Template" />
                    <span className="min-w-0 flex-1 truncate text-gray-900 dark:text-gray-100">{n.label}</span>
                    <span className="text-gray-500">{n.other_requests} other {n.other_requests === 1 ? 'request' : 'requests'}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {leftAlone.length > 0 && (
            <div className="rounded-lg bg-amber-50 dark:bg-amber-900/10 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
              <p className="font-semibold">Left where they are ({leftAlone.length})</p>
              <ul className="mt-1 max-h-40 space-y-0.5 overflow-auto">
                {leftAlone.map((b) => (
                  <li key={b.key}>
                    <span className="font-medium">{b.entity_type === 'template' ? 'Template' : 'Request'} {b.label}</span>: {b.message}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </PanelState>
    </Modal>
  )
}
